import { createSelector } from 'reselect'
import getCanShowCheckoutLoyaltyEsimBenefit from 'checkout/selectors/loyalty/esim/getCanShowCheckoutLoyaltyEsimBenefit'
import { getEsimUpsellTicketIdsFromTicket } from 'loyalty/lib/esims/esimUpsellTicketUtils'
import { formatEsimPackageLabel, formatEsimPackageTransitionLabel } from 'loyalty/utils/loyaltyEsimLabels'
import { knownPriceBreakdownTitle } from 'checkout/constants/priceBreakdownTitles'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { sum } from 'lib/array/arrayUtils'
import getExperienceItems from '../items/getExperienceItems'
import getExperienceItemsView from '../itemViews/getExperienceItemsView'
import isCheckoutEsimUpsellExperienceItem from 'loyalty/lib/esims/esimCheckoutUtils'

function getUpsellBreakdownLabel(itemView: App.Checkout.ExperienceItemView): string {
  const selectedTicket = itemView.item.tickets[0]
  if (!selectedTicket) return itemView.ticketSummaryViews[0]?.name ?? itemView.title

  const { toTicketId, fromTicketId } = getEsimUpsellTicketIdsFromTicket(selectedTicket)
  if (!toTicketId) return itemView.ticketSummaryViews[0]?.name ?? itemView.title

  const intl = getCurrentIntl()

  if (fromTicketId) {
    return formatEsimPackageTransitionLabel(intl, fromTicketId, toTicketId)
  }

  return formatEsimPackageLabel(intl, toTicketId)
}

const getLoyaltyEsimBreakdownView = createSelector(
  (state: App.State) => getCanShowCheckoutLoyaltyEsimBenefit(state),
  (state: App.State) => state.loyalty.account.data?.benefits,
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => getExperienceItemsView(state),
  (canShow, benefits, experienceItems, experienceItemsView): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    const paidUpsellInCart = experienceItems.some(item => isCheckoutEsimUpsellExperienceItem(item))
    if (paidUpsellInCart) {
      const paidUpsellViews = experienceItemsView.data.filter(itemView => isCheckoutEsimUpsellExperienceItem(itemView))
      if (!experienceItemsView.hasRequiredData && paidUpsellViews.length === 0) {
        return { hasRequiredData: false, data: [] }
      }

      return {
        hasRequiredData: true,
        data: [{
          title: knownPriceBreakdownTitle('additionalDataRoaming'),
          additionalInfoText: [],
          price: sum(paidUpsellViews.map(view => view.totals.price)),
          memberPrice: sum(paidUpsellViews.map(view => view.totals.memberPrice)),
          itemsAlwaysVisible: true,
          alwaysShowItemPrice: true,
          items: paidUpsellViews.map<App.Checkout.LoyaltyEsimBreakdownView>(expView => ({
            itemType: 'loyaltyEsimItem',
            title: '',
            additionalInfo: [],
            additionalElements: [{
              id: `paid-esim-${expView.item.itemId}`,
              left: {
                type: 'text',
                value: getUpsellBreakdownLabel(expView),
              },
              right: {
                type: 'price',
                value: expView.totals.price,
              },
            }],
            offerId: expView.experienceId,
            itemId: expView.item.itemId,
            itemView: expView,
            taxesAndFees: expView.totals.taxesAndFees,
          })),
        }],
      }
    }

    if (!canShow) {
      return { hasRequiredData: true, data: [] }
    }

    const esimBenefit = benefits?.find(b => b.code === 'esim_package')
    const gbValue = esimBenefit?.value?.amount
    if (!gbValue) {
      return { hasRequiredData: true, data: [] }
    }

    const intl = getCurrentIntl()

    return {
      data: [{
        title: knownPriceBreakdownTitle('travelEsim'),
        additionalInfoText: [],
        price: 0,
        memberPrice: 0,
        itemsAlwaysVisible: true,
        items: [{
          itemType: 'loyaltyEsimItem',
          title: '',
          additionalElements: [{
            id: 'esim',
            left: {
              type: 'text',
              value: intl.formatMessage(
                {
                  id: '5YUnRBFrYsxFGpee68j-',
                  defaultMessage: '1x {gbValue}GB travel eSIM',
                  description: 'eSIM item description in checkout summary breakdown',
                },
                { gbValue },
              ),
            },
            right: {
              type: 'price',
              value: 0,
            },
          }],
          additionalInfo: [],
        }],
      }],
      hasRequiredData: true,
    }
  },
)

export default getLoyaltyEsimBreakdownView
